import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAPI } from '../../API';
import { useAppDispatch, useAppState } from '../../Store';
import { useTranslation } from '../../Translation';
import SidebarNav from '../../Components/SidebarNav/SidebarNav';
import useAuthenticated from '../Dashboard/useAuthenticated';
import Table from '../../Components/Table/Table';
import LoadingCircleTransparent from '../../Components/LoadingIndicators/LoadingCircleTransparent';
import { usePaginations } from '../../Hooks/usePaginations';
import { useDateFormat } from '../../Hooks/useDateFormat';
import { useRefresh } from '../../Hooks/useRefresh';
import { TextInput } from '../../Components/Inputs/TextInput';
import { Button } from '../../Components/Buttons';
import Modal from '../../Components/Modal';
import Select from '../../Components/Inputs/Select';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
const useMapReportsForTable = (reports) => {
    const { formatDate } = useDateFormat();
    return reports.map((report) => {
        return {
            ...report,
            key: report.id,
            name: report.name,
            created_at: formatDate(report.created_at),
        };
    });
}

export default function Reports() {

    const { translate, language, setLanguage } = useTranslation();

    const { isAuthenticated, pathAfterLogin } = useAuthenticated();

    const api = useAPI();

    const appState = useAppState();
    const dispatch = useAppDispatch();
    const { sidebarSearch } = appState;
    const [reports, setReports] = useState([]);

    const { formatDate } = useDateFormat();

    const [fromDate, setFromDate] = useState(() => {
        const date = new Date();
        date.setDate(date.getDate() - 30);
        date.setHours(0, 0, 0, 0);
        return date;
    });
    const [toDate, setToDate] = useState(() => {
        const date = new Date();
        date.setHours(23, 59, 59, 999);
        return date;
    });

    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const pagination = usePaginations();
    const { refresh, refreshId } = useRefresh();
    const [open, setOpen] = useState(false);
    const [selectedReport, setSelectedReport] = useState(null);

    const [boards, setBoards] = useState([]);

    useEffect(() => {
        api.getBoards().then((response) => {
            setBoards(response.data.boards);
        });
    }, []);

    useEffect(() => {
        pagination.setPage(1);
    }, [sidebarSearch]);

    const handleSort = ({ key }) => {
        if (pagination.sortBy === key) {
            pagination.setSortOrder(pagination.sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            pagination.setSortBy(key);
            pagination.setSortOrder('asc');
        }
        pagination.setPage(1);
    }

    useEffect(() => {
        api.listReports(pagination.page, pagination.pageSize, sidebarSearch, pagination.sortBy, pagination.sortOrder).then((response) => {
            setReports(response.data.reports);
            pagination.setMetaData(response.data.meta);
            setLoading(false);
        });
    }, [pagination.page, pagination.sortBy, pagination.sortOrder, refreshId, sidebarSearch]);


    const setToDateEndOfDay = (date) => {
        // Create date in local timezone to avoid timezone offset issues
        const [year, month, day] = date.split('-').map(Number);
        const newDate = new Date(year, month - 1, day, 0, 0, 0);
        setToDate(newDate);
        console.log("toDate", newDate);
    }

    const setFromDateStartOfDay = (date) => {
        // Create date in local timezone to avoid timezone offset issues
        const [year, month, day] = date.split('-').map(Number);
        const newDate = new Date(year, month - 1, day, 0, 0, 0);
        setFromDate(newDate);
        console.log("fromDate", newDate);
    }

    const handleDelete = (row) => {
        api.deleteReport(row.id).then((response) => {
            refresh();
        });
    }

    const mappedReports = useMapReportsForTable(reports);

    return (
        <>
            <Modal open={open} onClose={() => setOpen(false)} title={translate('reports.create')} children={<ReportModal boards={boards} refresh={refresh} setOpen={setOpen} report={selectedReport} setSelectedReport={setSelectedReport} open={open} />} />
            <div className="flex min-h-full w-full">
                <SidebarNav currentPage="reports">
                    {loading ? <div className="flex justify-center items-center h-full w-12">
                        <LoadingCircleTransparent />
                    </div> : (
                        <>
                            <Table
                                title={translate('reports.title')}
                                description={translate('reports.description')}
                                data={mappedReports}
                                createButton={{
                                    text: translate('reports.create'),
                                    action: () =>  {
                                        setSelectedReport(null);
                                        setOpen(true);
                                    }
                                }}
                                dropdownGroups={[{
                                    options: [{
                                        label: translate('reports.edit'),
                                        icon: <PencilSquareIcon aria-hidden="true" className="mr-3 size-5 text-gray-400 group-data-[focus]:text-gray-500" />,
                                        action: (row) => {
                                            setSelectedReport(row);
                                            setOpen(true);
                                        }
                                    },{
                                        label: translate('reports.delete'),
                                        icon: <TrashIcon aria-hidden="true" className="mr-3 size-5 text-red-400 group-data-[focus]:text-red-500" />,
                                        action: (row) => {
                                            handleDelete(row);
                                        }
                                    }]
                                }]}
                                translationKey="reports.table"
                                columns={['name', 'created_at']}
                                pagination={pagination}
                                onSort={handleSort}
                                onRowClick={(row) => {
                                    navigate(`/report/${row.id}`);
                                }}
                            />
                        </>
                    )}
                </SidebarNav>
            </div>
        </>
    );
}

export const ReportModal = ({ boards, refresh, setOpen, report, setSelectedReport, open, isCampaign = false, tags }) => {

    const [name, setName] = useState(report ? report.name : '');
    const [boardId, setBoardId] = useState(report ? report.board_id : null);
    const [isActive, setIsActive] = useState(report ? report.is_active : false);
    const [numberOfBlocksInBoard, setNumberOfBlocksInBoard] = useState(report ? report.number_of_blocks_in_board : 2);
    const { translate } = useTranslation();
    const api = useAPI();
    const navigate = useNavigate();

    // If no board is selected, select the first board
    useEffect(() => {

        if (boards && boards.length > 0 && !boardId) {
            const tmpBoard = boards[0];
            setBoardId(tmpBoard.id);
        }
        
    }, [boards, boardId]);

    const numberOfBlocksInBoardOptions = [
        { label: '1', value: 1 },
        { label: '2', value: 2 },
        { label: '3', value: 3 },
        { label: '4', value: 4 },
    ];

    useEffect(() => {
        if (report) {
            setName(report.name);
            setBoardId(report.board_id);
            setIsActive(report.is_active);
        }
    }, [report]);

    const handleCreate = async () => {
        try {
            const response = await api.createReport({
                name,
                board_id: boardId,
                is_active: !isCampaign ? isActive : true,
                data: [],
                default_date_range: 'thirty_days',
                display_type: 'metric',
                type: 'insight',
                number_of_blocks_in_board: numberOfBlocksInBoard,
            });
            refresh();
            setOpen(false);
            setName('');
            setBoardId(null);

            navigate(`/report/${response.data.report.id}`);
        } catch (error) {
            console.error(error);
        }
    }

    const handleUpdate = async () => {
        try {
            await api.updateReport({
                ...report,
                name,
                board_id: boardId,
                is_active: !isCampaign ? isActive : true,
                number_of_blocks_in_board: numberOfBlocksInBoard,
            });
            refresh();
            setOpen(false);
        } catch (error) {
            console.error(error);
        }
    }
    

    const buttonDisabled = !name || name.length === 0 || !boardId;

    const selectedBoard = boards.find((board) => board.id === boardId) || boards[0];

    const onBoardSelect = (board) => {
        setBoardId(board.id);
    }

    const onNumberOfBlocksInBoardSelect = (option) => {
        setNumberOfBlocksInBoard(option.value);
    }


    return (
        <div className="mt-4 overflow-visible space-y-2">
            <TextInput id="name" name="name" label={translate('reports.name')} placeholder={translate('reports.name_placeholder')} value={name} onChange={(e) => setName(e.target.value)} />

            <Select title={translate('reports.board')} options={boards} selected={selectedBoard} setSelected={(board) => onBoardSelect(board)} />
            <Select title={translate('reports.number_of_blocks_in_board')} options={numberOfBlocksInBoardOptions} selected={numberOfBlocksInBoardOptions.find((option) => option.value === numberOfBlocksInBoard) || numberOfBlocksInBoardOptions[0]} setSelected={(option) => setNumberOfBlocksInBoard(option.value)} />

            <div className="flex justify-end mt-4">
                <Button disabled={buttonDisabled} onClick={report ? handleUpdate : handleCreate}>{report ? translate('update') : translate('create')}</Button>
            </div>
        </div>
    )
}